import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { format } from 'date-fns';
import { Button, TableCell, TableRow } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useRouter, usePathname } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/auth/hooks';

export const formatMarks = (val) => {
    if (val === null || val === undefined || val === '') return '0';
    if (typeof val === 'string' && val.toLowerCase() === 'ab') return 'Ab';
    const num = Number(val);
    if (isNaN(num)) return String(val);
    if (Number.isInteger(num) || num % 1 === 0) {
        return num.toString();
    }
    return String(val);
};

export const getSubjectDisplayName = (name) => {
    if (!name) return '';
    const upper = name.trim().toUpperCase();
    if (upper.startsWith('PHYS')) return 'PHY';
    if (upper.startsWith('CHEM')) return 'CHEM';
    if (upper.startsWith('ZOO')) return 'ZOO';
    if (upper.startsWith('BOT')) return 'BOT';
    if (upper.startsWith('MATH')) return 'MATH';
    return upper.length <= 4 ? upper : upper.slice(0, 4);
};

const ExaminationRow = ({ course, index, subjectsList = [], dense }) => {
    const {
        quiz = {},
        scored_marks,
        total_marks,
        subject_results = [],
        created_at,
    } = course;

    const router = useRouter();
    const pathname = usePathname();
    const { user } = useAuthContext();

    const handleGenerateAnalysisReport = useCallback(() => {
        const isHeightStudent = pathname?.includes('/heightstudentdashboard') || user?.is_height_student;
        const isParent = pathname?.includes('/parentdashboard');
        const isStudent = pathname?.includes('/studentdashboard');
        const isAcademic = pathname?.includes('/academicdashboard');

        const studentId =
            course.student?.id ||
            course.student_id ||
            course.height_student?.id ||
            course.height_student_id ||
            (typeof course.student === 'string' ? course.student : null) ||
            (isHeightStudent ? (user?.id || user?.height_student_id) : null);

        const queryParams = [];
        if (studentId) queryParams.push(`student_id=${studentId}`);
        if (isHeightStudent) queryParams.push('is_talent_hunt=true');
        const queryStr = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

        let reportUrl;
        if (isHeightStudent) {
            reportUrl = paths.heightstudentdashboard.examinationresult.profile(course.id);
        } else if (isParent) {
            reportUrl = paths.parentdashboard.examinationresult.profile(course.id);
        } else if (isStudent) {
            reportUrl = paths.studentdashboard.examinationresult.profile(course.id);
        } else if (isAcademic) {
            reportUrl = paths.academicdashboard.examinationresult.profile(course.id);
        } else {
            reportUrl = paths.dashboard.examinationresult.profile(course.id);
        }

        router.push(`${reportUrl}${queryStr}`);
    }, [course.id, course.student, course.student_id, course.height_student, course.height_student_id, pathname, user, router]);

    // Check if absent
    const isAbsent = Boolean(
        course.is_absent ||
        course.status?.toLowerCase() === 'absent' ||
        course.attendance?.toLowerCase() === 'absent' ||
        (Number(scored_marks) === 0 &&
         Number(total_marks) === 0 &&
         Array.isArray(subject_results) &&
         subject_results.length > 0 &&
         subject_results.every(
             (s) => String(s.scored_marks) === '0' || String(s.scored_marks).toLowerCase() === 'ab'
         ))
    );

    // Determine dynamic list of subjects
    const effectiveSubjects = subjectsList && subjectsList.length > 0
        ? subjectsList
        : (Array.isArray(subject_results) && subject_results.length > 0
            ? subject_results.map((sr) =>
                typeof sr?.subject === 'object' && sr?.subject !== null
                    ? (sr.subject.name || '')
                    : String(sr?.subject || '')
            ).filter(Boolean)
            : []);

    // Dynamic helper to extract score for any subject by name
    const getSubjectScore = (subjectName) => {
        if (isAbsent) return 'Ab';
        if (!Array.isArray(subject_results) || subject_results.length === 0) {
            return '';
        }

        const target = subject_results.find((res) => {
            const sName = typeof res?.subject === 'object' && res?.subject !== null
                ? (res.subject.name || '')
                : String(res?.subject || '');
            return sName.trim().toLowerCase() === subjectName.trim().toLowerCase();
        });

        if (!target) return '';
        if (target.is_absent || String(target.scored_marks).toLowerCase() === 'ab') {
            return 'Ab';
        }
        return formatMarks(target.scored_marks);
    };

    // Date formatting (dd/MM/yyyy)
    let formattedDate = '-';
    const rawDate =
        quiz?.start_datetime ||
        quiz?.start_date ||
        quiz?.date ||
        created_at;
    if (rawDate) {
        try {
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
                formattedDate = format(d, 'dd/MM/yyyy');
            }
        } catch (e) {
            formattedDate = String(rawDate);
        }
    }

    // Percentage calculation
    const calcPct = (() => {
        if (isAbsent && (!scored_marks || Number(scored_marks) === 0)) {
            return '0.00';
        }
        if (course.percentage !== undefined && course.percentage !== null && course.percentage !== '') {
            const p = Number(course.percentage);
            return isNaN(p) ? '0.00' : p.toFixed(2);
        }
        const s = Number(scored_marks);
        const t = Number(total_marks);
        if (!t || isNaN(s) || isNaN(t) || t <= 0) return '0.00';
        return ((s / t) * 100).toFixed(2);
    })();

    // Rank
    const displayRank = isAbsent && (!course.rank || course.rank === 'N/A')
        ? '0'
        : (course.rank && course.rank !== 'N/A' ? String(course.rank) : '0');

    // Total Marks Display
    const displayObtMarks = isAbsent && (!scored_marks || Number(scored_marks) === 0)
        ? '0'
        : formatMarks(scored_marks);

    const displayMaxMarks = isAbsent && (!total_marks || Number(total_marks) === 0)
        ? '0'
        : formatMarks(total_marks);

    const cellStyle = {
        border: (theme) => `1px solid ${theme.palette.divider}`,
        whiteSpace: 'nowrap',
    };

    return (
        <TableRow
            hover
            style={{ height: dense ? '48px' : '64px' }}
            sx={{
                '& td, & th': {
                    border: (theme) => `1px solid ${theme.palette.divider} !important`,
                },
            }}
        >
            {/* 1. No. */}
            <TableCell align="center" sx={cellStyle}>
                {index !== undefined ? index + 1 : (course.s_no || 1)}
            </TableCell>

            {/* 2. Test Date */}
            <TableCell align="center" sx={cellStyle}>
                {formattedDate}
            </TableCell>

            {/* 3. Dynamic Subject Marks Cells */}
            {effectiveSubjects.length > 0 ? (
                effectiveSubjects.map((subj) => {
                    const score = getSubjectScore(subj);
                    return (
                        <TableCell
                            key={subj}
                            align="center"
                            sx={cellStyle}
                        >
                            {score || '-'} 
                        </TableCell>
                    );
                })
            ) : (
                <TableCell align="center" sx={cellStyle}>
                    {isAbsent ? 'Ab' : '-'}
                </TableCell>
            )}

            {/* 4. Total Marks Obt. */}
            <TableCell align="center" sx={cellStyle}>
                {displayObtMarks}
            </TableCell>

            {/* 5. Total Marks Max */}
            <TableCell align="center" sx={cellStyle}>
                {displayMaxMarks}
            </TableCell>

            {/* 6. % */}
            <TableCell align="center" sx={cellStyle}>
                {calcPct}
            </TableCell>

            {/* 8. Action (Only Generate Analysis Report button) */}
            <TableCell align="center" sx={cellStyle}>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Iconify icon="solar:document-bold-duotone" />}
                    onClick={handleGenerateAnalysisReport}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Generate Analysis Report
                </Button>
            </TableCell>
        </TableRow>
    );
};

ExaminationRow.propTypes = {
    course: PropTypes.object.isRequired,
    index: PropTypes.number,
    subjectsList: PropTypes.array,
    dense: PropTypes.bool,
    onEditRow: PropTypes.func,
    onDeleteRow: PropTypes.func,
};

export default ExaminationRow;
